import { Text } from "@clipboard-health/ui-components";
import { Alert, Box, DialogContent, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { FormProvider } from "react-hook-form";

import { PostShiftReviewHeader } from "./components/PostShiftReviewHeader";
import { usePostShiftReviewNavigation } from "./hooks/usePostShiftReviewNavigation";
import { usePostShiftReviewSteps } from "./hooks/usePostShiftReviewSteps";
import { CompletionStep } from "./steps/CompletionStep";
import { ParkingQuestionStep } from "./steps/ParkingQuestionStep";
import { QuestionStep } from "./steps/QuestionStep";
import { RateWorkplaceStep } from "./steps/RateWorkplaceStep";
import { WriteReviewStep } from "./steps/WriteReviewStep";
import { type PostShiftReviewFlowContentProps, QuestionTag } from "./types";
import { usePostShiftReviewForm } from "./usePostShiftReviewForm";

export function PostShiftReviewFlowContent({
  modalState,
  facility,
  onComplete,
  shiftId,
  questionMappings,
  isLoading = false,
  isError = false,
  onSubmitRating,
  onSubmitReview,
}: PostShiftReviewFlowContentProps) {
  const { form } = usePostShiftReviewForm({
    onSubmitRating,
    onSubmitReview,
  });

  const { stepTypes, getStepConfig } = usePostShiftReviewSteps({
    questionMappings,
    isError,
  });

  const { currentStep, handleStepAction, isSubmittingRatingLocally, isSubmittingReviewLocally } =
    usePostShiftReviewNavigation({
      stepTypes,
      form,
      onSubmitRating,
      onSubmitReview,
      onComplete,
      closeModal: modalState.closeModal,
      shiftId,
    });

  useLogEffect(
    APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_STEP_VIEWED,
    {
      step: currentStep,
      shiftId,
    },
    { enabled: modalState.modalIsOpen }
  );

  if (isLoading) {
    return (
      <FullScreenDialog
        modalState={modalState}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.tertiary,
          }),
        }}
      >
        <PostShiftReviewHeader
          currentStep="pick-rating"
          computedSteps={["pick-rating", "write-review", "end"]}
          onBackOrClose={handleStepAction}
        />
        <DialogContent>
          <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
            <Text>Loading...</Text>
          </Stack>
        </DialogContent>
      </FullScreenDialog>
    );
  }

  return (
    <FormProvider {...form}>
      <FullScreenDialog
        modalState={modalState}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.tertiary,
          }),
        }}
      >
        <PostShiftReviewHeader
          currentStep={currentStep}
          computedSteps={stepTypes}
          onBackOrClose={handleStepAction}
        />

        {isError && (
          <Alert severity="error" sx={{ my: 2 }}>
            Unable to load review questions. Some steps may be unavailable.
          </Alert>
        )}

        <Box
          sx={(theme) => ({
            flex: 1,
            display: "flex",
            flexDirection: "column",
            mt: 5,
            p: 0,
            backgroundColor: theme.palette.background.tertiary,
          })}
        >
          {currentStep === "pick-rating" && (
            <RateWorkplaceStep
              facilityName={facility.name}
              facilityPhoto={facility.photo}
              isSubmitting={isSubmittingRatingLocally}
              onAction={handleStepAction}
            />
          )}
          {currentStep === "write-review" && (
            <WriteReviewStep isSubmitting={isSubmittingReviewLocally} onAction={handleStepAction} />
          )}
          {(currentStep === QuestionTag.SOFTWARE ||
            currentStep === QuestionTag.SUPPLIES ||
            currentStep === QuestionTag.PATIENTS_PER_NURSE) && (
            <QuestionStep
              questionTag={currentStep}
              questionText={getStepConfig(currentStep)?.questionText ?? ""}
              options={getStepConfig(currentStep)?.options}
              isSubmitting={isSubmittingReviewLocally}
              onAction={handleStepAction}
            />
          )}
          {currentStep === QuestionTag.PARKING && (
            <ParkingQuestionStep
              isSubmitting={isSubmittingReviewLocally}
              onAction={handleStepAction}
            />
          )}
          {currentStep === "end" && <CompletionStep onAction={handleStepAction} />}
        </Box>
      </FullScreenDialog>
    </FormProvider>
  );
}
